import { PricingInfo } from '../types/pricing';
import { LicensePlan } from '../types/license';

const coreCopilotFeatures = [
  {
    name: 'Microsoft 365 Copilot',
    included: true,
    additionalInfo: 'AI-powered assistance across Microsoft 365 apps'
  },
  {
    name: 'Copilot in Word',
    included: true,
    additionalInfo: 'Draft, edit, summarize, and improve writing'
  },
  {
    name: 'Copilot in Excel',
    included: true,
    additionalInfo: 'Analyze data and create visualizations'
  },
  {
    name: 'Copilot in PowerPoint',
    included: true,
    additionalInfo: 'Create and enhance presentations'
  },
  {
    name: 'Copilot in Outlook',
    included: true,
    additionalInfo: 'Compose and summarize emails'
  },
  {
    name: 'Copilot in Teams',
    included: true,
    additionalInfo: 'Meeting summaries and real-time assistance'
  }
];

const businessCopilotFeatures = [
  { name: 'Work IQ', included: true, additionalInfo: 'Work-grounded context across Microsoft 365' },
  { name: 'Researcher agent', included: true, additionalInfo: 'Research and synthesis agent' },
  { name: 'Analyst agent', included: true, additionalInfo: 'Data analysis and insights agent' },
  { name: 'Facilitator agent', included: true, additionalInfo: 'Meeting and collaboration agent' },
  { name: 'Agent Builder and Copilot Studio', included: true, additionalInfo: 'Create and use agents' },
  { name: 'Copilot connectors', included: true, additionalInfo: 'Connect external data sources' },
  { name: 'Copilot Cowork', included: true, additionalInfo: 'Access included; usage is metered' },
];

const hasCopilotIncluded = (plan: LicensePlan): boolean => {
  return plan.features.some(f => f.name === 'Microsoft 365 Copilot' && f.included);
};

const hasTeamsIncluded = (plan: LicensePlan): boolean =>
  plan.features.some(
    feature =>
      (feature.name === 'Microsoft Teams' || feature.name === 'Teams') &&
      feature.included
  );

const getCopilotFeatures = (plan: LicensePlan) => {
  const features = plan.id.startsWith('m365-business')
    ? [...coreCopilotFeatures, ...businessCopilotFeatures]
    : coreCopilotFeatures;

  return features.map(feature =>
    feature.name === 'Copilot in Teams'
      ? {
          ...feature,
          included: hasTeamsIncluded(plan),
          additionalInfo: hasTeamsIncluded(plan)
            ? feature.additionalInfo
            : 'Requires a separate Microsoft Teams license',
        }
      : feature
  );
};

const mergeCopilotFeatures = (
  planFeatures: LicensePlan['features'],
  copilotFeatures: LicensePlan['features']
): LicensePlan['features'] => {
  const copilotByName = new Map(copilotFeatures.map(feature => [feature.name, feature]));
  const merged = planFeatures.map(feature => copilotByName.get(feature.name) ?? feature);
  const existingNames = new Set(planFeatures.map(feature => feature.name));

  return [
    ...merged,
    ...copilotFeatures.filter(feature => !existingNames.has(feature.name)),
  ];
};

const addPriceToPlan = (priceStr: string, amount: number): string => {
  const match = priceStr.match(/\$([\d.]+)/);
  if (!match) return priceStr;
  const newPrice = parseFloat(match[1]) + amount;
  return `$${newPrice.toFixed(2)} user/month`;
};

/** Add an add-on price to every point in a series. Operates on numbers, so
 *  audience variants and non-quotable offers survive intact — the old string
 *  regex silently dropped the faculty price on Education plans. */
const addPriceToPricing = (pricing: PricingInfo, amount: number): PricingInfo => ({
  points: pricing.points.map(pt =>
    pt.amount === null ? pt : { ...pt, amount: Number((pt.amount + amount).toFixed(2)) }
  ),
});

export const addCopilotFeatures = (plan: LicensePlan): LicensePlan => {
  if (hasCopilotIncluded(plan)) {
    return plan;
  }

  if (!plan.name.includes('E3') && !plan.name.includes('E5') && !plan.name.includes('E7') &&
      !plan.name.includes('Business Standard') && !plan.name.includes('Business Premium')) {
    return {
      ...plan,
      features: [
        ...plan.features,
        { name: 'Microsoft 365 Copilot', included: false, additionalInfo: 'Not available for this plan' }
      ]
    };
  }

  const addOnPrice = plan.id.startsWith('m365-business') ? 21 : 30;
  const updatedPricing = plan.pricing
    ? addPriceToPricing(plan.pricing, addOnPrice)
    : undefined;
  const copilotFeatures = getCopilotFeatures(plan);

  return {
    ...plan,
    price: plan.price ? addPriceToPlan(plan.price, addOnPrice) : undefined,
    pricing: updatedPricing ?? plan.pricing,
    features: mergeCopilotFeatures(plan.features, copilotFeatures)
  };
};